// LAS FILAS QUE PINTA EL "HISTORIAL DE MACROS" DE LA FICHA (prueba 14 de Macros, 12-10).
//
// El historial guarda UNA fila por cliente y fecha de vigencia: si el mismo día se guarda
// dos veces (el cuestionario del alta y luego un ajuste a mano), manda la última y la
// anterior queda resumida dentro de ella, en `versiones_del_dia` (`core/historial_macros.py`,
// de la más antigua a la más reciente). La tabla enseñaba eso como una nota bajo la fecha y
// quien prueba esperaba VER la fila anterior. Aquí se decide, sin pantalla de por medio:
//   - qué filas se pintan: cada fila real y, debajo, las versiones que sustituyó ese día,
//     de la más reciente a la más antigua (el mismo orden que el resto de la tabla);
//   - contra cuál se compara cada una para marcar en rojo lo que cambió: siempre contra la
//     que tiene justo debajo, que ahora puede ser una versión del mismo día.

// La fecha de vigencia de una fila, 'AAAA-MM-DD': `effective_date` y, si no, el día en que
// se guardó (la misma clave que usa el servidor, `core/historial_macros.fecha_de_vigencia`).
export const fechaDeLaFila = (h) => h?.effective_date || (h?.created_at || '').slice(0, 10) || '';

export const versionesDelDia = (fila) =>
    (Array.isArray(fila?.versiones_del_dia) ? fila.versiones_del_dia.filter(v => v && typeof v === 'object') : []);

const _conGramos = (v) => !!v && [v.training, v.rest].some(m => m && typeof m === 'object');

/**
 * `visibles`: las filas reales que se van a enseñar, de la más reciente a la más antigua.
 * `todas`: la lista entera en ese mismo orden (por si `visibles` es un recorte: la última
 * fila a la vista se compara con la siguiente aunque no se enseñe).
 *
 * Devuelve una entrada por fila pintada: { fila, anterior, cambios, sustituida, clave }.
 *   - `anterior`: la fila o versión con la que se compara (null si no hay).
 *   - `cambios`: el cambio GUARDADO en la fila, cuando vale. En una fila que sustituyó a otra
 *     ese día no vale: se guardó contra lo que había ANTES de ese día, y lo que se quiere ver
 *     es qué cambió respecto a la versión de justo debajo. Ahí va null y se compara.
 *   - `sustituida`: true en las versiones del día; no son filas del historial (no se editan,
 *     no se repiten, no se evalúan).
 */
export function filasQueSePintan(visibles, todas = visibles) {
    const salida = [];
    for (const fila of (visibles || [])) {
        const i = (todas || []).indexOf(fila);
        const deAntes = i >= 0 ? (todas[i + 1] || null) : null;
        // De la más reciente a la más antigua.
        const versiones = versionesDelDia(fila).slice().reverse();
        const primera = versiones[0];
        const clave = fila.id || `fila-${i}`;
        salida.push({
            fila,
            anterior: _conGramos(primera) ? primera : deAntes,
            cambios: _conGramos(primera) ? null : (fila.cambios || null),
            sustituida: false,
            clave,
        });
        versiones.forEach((v, n) => {
            salida.push({
                // La versión no guarda su fecha de vigencia: es la de la fila que la sustituyó.
                fila: { ...v, effective_date: fechaDeLaFila(fila) },
                anterior: versiones[n + 1] || deAntes,
                cambios: null,
                sustituida: true,
                clave: `${clave}-v${versiones.length - n}`,
            });
        });
    }
    return salida;
}

// El origen de una fila, con el nombre corto que se enseña al pasar por la fecha. Lo que no
// se reconoce no se inventa: null y la fila no dice nada. Las migradas de Calma tampoco dicen
// nada (vídeo de Jesús, 20-10: «esto de importado de Calma, de verdad, elimínalo»).
const ORIGEN = {
    manual: 'manual',
    quiz_alta: 'cuestionario de alta',
    quiz_ajuste: 'cuestionario del cliente',
    cliente_calculadora: 'calculadora del cliente',
    coach_calculadora: 'calculadora del equipo',
    revision_suelta: 'revisión suelta',
    ia: 'IA',
    ia_corregida: 'IA corregida',
};
export function nombreDelOrigen(fila) {
    if (fila?.calma_migrated || fila?.changed_by === 'migracion') return null;
    return ORIGEN[fila?.origen] || null;
}

const _pesoDeLaFila = (h) => { const p = h?.peso ?? h?.client_weight; return typeof p === 'number' ? p : null; };

/**
 * EL PESO DEL ÚLTIMO AJUSTE: el último guardado con unos macros. Es contra el que compara
 * el editor («Últimos macros: 78,4 kg»), y el que trae si no hay ningún mensual con peso. `{ peso, fecha }` (la fecha de su pesaje, o
 * la de la fila si no la guardó), o null si ningún ajuste lo lleva: el campo va vacío.
 */
const _pesoConFecha = (h) => {
    const peso = _pesoDeLaFila(h);
    return peso == null ? null : { peso, fecha: String(h.peso_fecha || fechaDeLaFila(h)).slice(0, 10) };
};

export function ultimoPesoDelHistorial(historial) {
    const orden = (historial || []).filter(Boolean).slice()
        .sort((a, b) => fechaDeLaFila(b).localeCompare(fechaDeLaFila(a)));
    for (const h of orden) {
        const conFecha = _pesoConFecha(h);
        if (conFecha) return conFecha;
    }
    return null;
}

const _esMensual = (r) => ['mensual', null, undefined, ''].includes(r?.tipo);

/**
 * EL PESO DEL REPORTE MENSUAL ACTUAL (21-10): el del mensual más reciente que lo trae, con
 * el día de ese peso (`dia_del_peso` o `peso_fecha`, 31-10; en los viejos, `diaDe`, el día de
 * España de su `created_at`, como `dia_del_peso_del_reporte` del servidor). `{ peso, fecha }`,
 * o null si ningún mensual lleva peso. Los quincenales no cuentan.
 */
export function pesoDelUltimoMensual(reports, diaDe = (v) => String(v || '').slice(0, 10)) {
    const conPeso = (reports || []).filter(r => _esMensual(r) && r?.created_at
        && typeof r.weight === 'number' && Number.isFinite(r.weight));
    if (!conPeso.length) return null;
    const ultimo = conPeso.slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
    return { peso: ultimo.weight, fecha: ultimo.dia_del_peso || ultimo.peso_fecha || diaDe(ultimo.created_at) || '' };
}

/** EL PESO CON EL QUE ARRANCA EL EDITOR DE MACROS (21-10): el del reporte mensual actual; si
 *  no hay ninguno con peso, el del último ajuste que lo lleva. `{ peso, fecha }` o null. */
export const pesoParaElEditor = (reports, historial, diaDe) =>
    pesoDelUltimoMensual(reports, diaDe) || ultimoPesoDelHistorial(historial);

/** La fila que rige hoy (la «ACTUAL» del histórico): la más reciente que ni está
 *  programada para más adelante ni es el ajuste sin guardar. `filas`, de la más reciente a
 *  la más antigua. */
export const laQueRige = (filas) => (filas || []).find(h => h && !h._programado && !h._borrador) || null;

/**
 * EL PESO CON EL QUE SE CALCULARON SUS ÚLTIMOS MACROS (Francisco, 21-10, sección «Peso» de
 * Seguimiento): el de la fila que rige, `{ peso, fecha }`. Si esa fila no guardó peso, null
 * («sin dato»): el de un ajuste anterior no es el de estos macros. No es lo mismo que
 * `ultimoPesoDelHistorial`, que busca hacia atrás para precargar el editor.
 */
export const pesoDeLosUltimosMacros = (filas) => {
    const fila = laQueRige(filas);
    return fila ? _pesoConFecha(fila) : null;
};
